import { useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Loader2,
  Building2,
  ImageIcon,
  KeyRound,
  Copy,
  MapPin,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Partner,
  Branch,
  regeneratePartnerSyncSecret,
  revokePartnerSyncSecret,
  createPartner,
  updatePartner,
  deletePartner,
  createBranch,
  updateBranch,
  deleteBranch,
} from "../api/api";
import { usePartners, useBranches, useKams } from "../hooks/queries";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Pill } from "../components/ui/pill";
import { SectionLabel } from "../components/ui/section-label";
import { EmptyState } from "../components/ui/empty-state";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "../lib/toast";
import { partnerIconUrl } from "../lib/partner";
import { cn } from "@/lib/utils";

interface EditingState {
  type: "branch" | "partner";
  id: number;
  value: string;
  location: string;
}

interface AddingBranchState {
  partnerId: number;
  name: string;
  location: string;
}

interface ConfirmState {
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
}

function initialsOf(name: string): string {
  return (name || "??").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

/** Partner icon (icon_url, else logo_url) with an initials chip as fallback. */
function PartnerLogo({ partner, size = "md" }: { partner: Partner; size?: "md" | "lg" }) {
  const [failed, setFailed] = useState(false);
  const box = size === "lg" ? "h-14 w-14 text-base" : "h-10 w-10 text-[13px]";
  const iconUrl = partnerIconUrl(partner);
  if (iconUrl && !failed) {
    return (
      <div className={cn("flex shrink-0 items-center justify-center overflow-hidden rounded-nav border border-waves-line bg-white p-1", box)}>
        <img
          src={iconUrl}
          alt={partner.name}
          className="max-h-full max-w-full object-contain"
          onError={() => setFailed(true)}
        />
      </div>
    );
  }
  return (
    <div className={cn("flex shrink-0 items-center justify-center rounded-nav bg-waves-lucht font-bold text-waves-ink", box)}>
      {initialsOf(partner.name)}
    </div>
  );
}

/**
 * View/edit row for one of a partner's image URLs. With `square`, a preview
 * whose width and height differ blocks saving. Give it a per-partner `key`
 * so an open edit doesn't carry over to another partner.
 */
function ImageUrlField({
  label,
  value,
  square = false,
  saving,
  onSave,
}: {
  label: string;
  value: string | null | undefined;
  square?: boolean;
  saving: boolean;
  /** Resolves true once saved, which closes the editor. */
  onSave: (url: string) => Promise<boolean>;
}) {
  const [input, setInput] = useState<string | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const url = input?.trim() ?? "";
  const notSquare = square && url !== "" && dims !== null && dims.w !== dims.h;

  async function save() {
    if (notSquare || saving) return;
    if (await onSave(url)) setInput(null);
  }

  return (
    <div className="flex flex-col gap-2">
      <Label>{label}</Label>
      {input !== null ? (
        <div className="flex flex-col gap-3">
          <Input
            value={input}
            onChange={(e) => { setInput(e.target.value); setDims(null); }}
            placeholder="https://…"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") setInput(null);
            }}
          />
          {notSquare && (
            <p className="text-xs font-medium text-status-alert-fg">
              Het icoon moet vierkant zijn — deze afbeelding is {dims!.w}×{dims!.h} px.
            </p>
          )}
          <div className="flex items-center gap-2">
            {url && (
              <img
                key={url}
                src={url}
                alt="preview"
                className={cn("h-9 rounded-nav border border-waves-line bg-white object-contain p-1", square ? "w-9" : "w-auto")}
                onLoad={(e) => setDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setInput(null)}>
                Annuleren
              </Button>
              <Button variant="ink" size="sm" onClick={save} disabled={saving || notSquare}>
                {saving ? <Loader2 className="animate-spin" /> : <Check />}
                Opslaan
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          {value ? (
            <img
              src={value}
              alt={label}
              className={cn("h-9 rounded-nav border border-waves-line bg-white object-contain p-1", square ? "w-9" : "w-auto")}
            />
          ) : (
            <span className="text-sm italic text-waves-muted">Niet ingesteld</span>
          )}
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => { setDims(null); setInput(value ?? ""); }}>
            <Pencil />
            Wijzigen
          </Button>
        </div>
      )}
    </div>
  );
}

export default function PartnerConfig() {
  const queryClient = useQueryClient();
  const { data: partners = [], isLoading, error: queryError } = usePartners(true, { silenceErrors: true });
  const { data: branches = [] } = useBranches();
  const { data: kams = [] } = useKams();
  const loading = isLoading;
  const error = queryError instanceof Error ? queryError.message : queryError ? "Laden mislukt." : "";

  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [addingBranch, setAddingBranch] = useState<AddingBranchState | null>(null);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [newPartnerName, setNewPartnerName] = useState("");
  const [newPartnerEmailDomain, setNewPartnerEmailDomain] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [syncSecretBusy, setSyncSecretBusy] = useState(false);
  const [revealedSyncSecret, setRevealedSyncSecret] = useState<string | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

  const activePartnerId = selectedPartnerId ?? (partners[0]?.id ?? null);
  const activePartner = partners.find((p) => p.id === activePartnerId) ?? null;
  const partnerBranches = branches.filter((t) => t.partner_id === activePartnerId);

  const kamNameById = new Map(kams.map((k) => [k.id, k.name]));
  const kamNamesFor = (p: Partner) =>
    (p.kam_ids ?? []).map((id) => kamNameById.get(id)).filter((n): n is string => !!n);
  const branchCountFor = (p: Partner) => branches.filter((b) => b.partner_id === p.id).length;

  async function handleSaveBranch(branch: Branch) {
    if (!editing || editing.value.trim() === "") return;
    if (editing.location.trim() === "") {
      setFormError("Locatienaam is verplicht.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await updateBranch(branch.id, {
        name: editing.value.trim(),
        location: editing.location.trim(),
      }, { silenceErrors: true });
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["branches"] });
      toast("Vestiging opgeslagen", { kind: "success" });
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteBranch(branch: Branch) {
    try {
      await deleteBranch(branch.id);
      queryClient.invalidateQueries({ queryKey: ["branches"] });
      toast("Vestiging verwijderd", { kind: "success" });
    } catch {
      // handled by the global error snackbar
    }
  }

  async function handleAddBranch() {
    if (!addingBranch || !addingBranch.name.trim()) {
      setFormError("Vestigingsnaam is verplicht.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await createBranch({
        partner_id: addingBranch.partnerId,
        name: addingBranch.name.trim(),
        location: addingBranch.location.trim() || addingBranch.name.trim(),
      }, { silenceErrors: true });
      setAddingBranch(null);
      queryClient.invalidateQueries({ queryKey: ["branches"] });
      toast("Vestiging toegevoegd", { kind: "success" });
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Toevoegen mislukt.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveImage(partner: Partner, field: "logo_url" | "icon_url", url: string): Promise<boolean> {
    setSaving(true);
    setFormError("");
    try {
      await updatePartner(partner.id, { [field]: url }, { silenceErrors: true });
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast(field === "logo_url" ? "Logo opgeslagen" : "Icoon opgeslagen", { kind: "success" });
      return true;
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Opslaan mislukt.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function doRegenerateSyncSecret(partner: Partner) {
    setSyncSecretBusy(true);
    setFormError("");
    try {
      const { sync_secret } = await regeneratePartnerSyncSecret(partner.id);
      setRevealedSyncSecret(sync_secret);
      setSecretCopied(false);
      queryClient.invalidateQueries({ queryKey: ["partners"] });
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Genereren mislukt.");
    } finally {
      setSyncSecretBusy(false);
    }
  }

  function handleRegenerateSyncSecret(partner: Partner) {
    if (partner.has_sync_secret) {
      setConfirmState({
        title: "Sync secret vernieuwen?",
        description: `Sync secret voor "${partner.name}" vernieuwen? De vorige waarde stopt direct met werken totdat het Apps Script bijgewerkt is.`,
        confirmLabel: "Vernieuwen",
        onConfirm: () => doRegenerateSyncSecret(partner),
      });
      return;
    }
    doRegenerateSyncSecret(partner);
  }

  async function handleRevokeSyncSecret(partner: Partner) {
    setSyncSecretBusy(true);
    setFormError("");
    try {
      await revokePartnerSyncSecret(partner.id);
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast("Sync secret ingetrokken", { kind: "success" });
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Intrekken mislukt.");
    } finally {
      setSyncSecretBusy(false);
    }
  }

  async function handleDeletePartner(partner: Partner) {
    try {
      await deletePartner(partner.id);
      if (selectedPartnerId === partner.id) setSelectedPartnerId(null);
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      queryClient.invalidateQueries({ queryKey: ["branches"] });
      toast("Partner verwijderd", { kind: "success" });
    } catch {
      // handled by the global error snackbar
    }
  }

  async function handleAddPartner() {
    if (!newPartnerName.trim()) {
      setFormError("Partnernaam is verplicht.");
      return;
    }
    if (!newPartnerEmailDomain.trim()) {
      setFormError("E-maildomein is verplicht.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const newPartner = await createPartner({
        name: newPartnerName.trim(),
        email_domain: newPartnerEmailDomain.trim(),
      }, { silenceErrors: true });
      setSelectedPartnerId(newPartner.id);
      setShowPartnerDialog(false);
      setNewPartnerName("");
      setNewPartnerEmailDomain("");
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast("Partner aangemaakt", { kind: "success" });
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Toevoegen mislukt.");
    } finally {
      setSaving(false);
    }
  }

  function closePartnerDialog() {
    setShowPartnerDialog(false);
    setNewPartnerEmailDomain("");
    setNewPartnerName("");
  }

  function selectPartner(id: number) {
    setSelectedPartnerId(id);
    setAddingBranch(null);
    setEditing(null);
    setFormError("");
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-waves-zee" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto flex max-w-[1240px] flex-col gap-5">
        <div className="rounded-nav bg-status-alert-bg px-4 py-2.5 text-sm font-medium text-status-alert-fg">
          <strong>Fout bij laden:</strong> {error}
        </div>
      </div>
    );
  }

  const isEditingBranch = (b: Branch) => editing?.type === "branch" && editing.id === b.id;
  const isAddingHere = activePartner != null && addingBranch?.partnerId === activePartner.id;

  return (
    <div className="mx-auto flex max-w-[1240px] flex-col gap-5">
      {/* Header actions */}
      <div className="flex justify-end">
        <Button
          onClick={() => {
            setFormError("");
            setNewPartnerName("");
            setNewPartnerEmailDomain("");
            setShowPartnerDialog(true);
          }}
        >
          <Plus />
          Partner toevoegen
        </Button>
      </div>

      {partners.length === 0 ? (
        <div className="rounded-card border border-waves-line bg-white">
          <EmptyState
            icon={<Building2 />}
            title="Geen partners gevonden"
            description="Voeg een partner toe om te beginnen."
          />
        </div>
      ) : (
        <div className="grid items-start gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
          {/* Partner list */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <SectionLabel>Partners</SectionLabel>
              <span className="rounded-pill bg-waves-mist px-2.5 py-0.5 font-mono text-xs font-semibold">{partners.length}</span>
            </div>
            <div className="flex flex-col gap-2">
              {partners.map((partner) => {
                const active = activePartnerId === partner.id;
                const kamNames = kamNamesFor(partner);
                const count = branchCountFor(partner);
                return (
                  <button
                    key={partner.id}
                    onClick={() => selectPartner(partner.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-[16px] border bg-white px-4 py-3.5 text-left transition-colors",
                      active
                        ? "border-waves-zee bg-waves-mist"
                        : "border-waves-line hover:bg-waves-subtle",
                    )}
                  >
                    <PartnerLogo partner={partner} />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="truncate text-[15px] font-bold text-waves-ink">{partner.name}</div>
                      <div className="truncate font-mono text-xs text-waves-muted">@{partner.email_domain}</div>
                      <div className="truncate text-xs text-waves-muted">
                        {kamNames.length > 0 ? `KAM: ${kamNames.join(", ")}` : "Geen KAM"}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end">
                      <span className="font-mono text-sm font-semibold text-waves-ink">{count}</span>
                      <span className="text-[11px] text-waves-muted">{count === 1 ? "vestiging" : "vestigingen"}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Partner detail */}
          {activePartner && (
            <div className="flex min-w-0 flex-col gap-5">
              {/* Header card */}
              <div className="flex flex-wrap items-center gap-4 rounded-card border border-waves-line bg-white p-6">
                <PartnerLogo partner={activePartner} size="lg" />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <h2 className="truncate text-lg font-black tracking-[-0.01em]">{activePartner.name}</h2>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-waves-muted">
                    <span className="font-mono">@{activePartner.email_domain}</span>
                    <span>
                      <span className="font-mono">{partnerBranches.length}</span> vestiging{partnerBranches.length !== 1 ? "en" : ""}
                    </span>
                    {kamNamesFor(activePartner).length > 0 && (
                      <span>KAM: {kamNamesFor(activePartner).join(", ")}</span>
                    )}
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() =>
                    setConfirmState({
                      title: "Partner verwijderen?",
                      description: `Partner "${activePartner.name}" verwijderen? Alle bijbehorende vestigingen worden ook verwijderd.`,
                      confirmLabel: "Partner verwijderen",
                      onConfirm: () => handleDeletePartner(activePartner),
                    })
                  }
                >
                  <Trash2 />
                  Partner verwijderen
                </Button>
              </div>

              {formError && !showPartnerDialog && (
                <div className="rounded-nav bg-status-alert-bg px-4 py-2.5 text-sm font-medium text-status-alert-fg">{formError}</div>
              )}

              {/* Logo + sync secret */}
              <div className="grid gap-5 md:grid-cols-2">
                <div className="flex flex-col gap-4 rounded-card border border-waves-line bg-white p-6">
                  <div className="flex items-start gap-3">
                    <ImageIcon className="mt-0.5 h-4 w-4 shrink-0 text-waves-muted" />
                    <div>
                      <h3 className="text-lg font-black tracking-[-0.01em]">Logo</h3>
                      <p className="text-sm text-waves-muted">Afbeelding-URL's van het partnerlogo. Het vierkante icoon wordt gebruikt op kleine plekken (menu, chat, tabellen); zonder icoon valt dat terug op het logo.</p>
                    </div>
                  </div>
                  <ImageUrlField
                    key={`logo-${activePartner.id}`}
                    label="Logo (breed)"
                    value={activePartner.logo_url}
                    saving={saving}
                    onSave={(url) => handleSaveImage(activePartner, "logo_url", url)}
                  />
                  <ImageUrlField
                    key={`icon-${activePartner.id}`}
                    label="Icoon (vierkant)"
                    value={activePartner.icon_url}
                    square
                    saving={saving}
                    onSave={(url) => handleSaveImage(activePartner, "icon_url", url)}
                  />
                </div>

                <div className="flex flex-col gap-4 rounded-card border border-waves-line bg-white p-6">
                  <div className="flex items-start gap-3">
                    <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-waves-muted" />
                    <div>
                      <h3 className="text-lg font-black tracking-[-0.01em]">Google Sheets sync</h3>
                      <p className="text-sm text-waves-muted">Secret voor het Apps Script van deze partner.</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {activePartner.has_sync_secret ? (
                      <Pill kind="health-ok">Geconfigureerd</Pill>
                    ) : (
                      <Pill kind="health-unconfigured">Niet ingesteld</Pill>
                    )}
                    <div className="ml-auto flex gap-2">
                      {activePartner.has_sync_secret && (
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={syncSecretBusy}
                          onClick={() =>
                            setConfirmState({
                              title: "Sync secret intrekken?",
                              description: `Sync secret voor "${activePartner.name}" intrekken? De sheet-synchronisatie stopt direct met werken voor deze partner.`,
                              confirmLabel: "Intrekken",
                              onConfirm: () => handleRevokeSyncSecret(activePartner),
                            })
                          }
                        >
                          <Trash2 />
                          Intrekken
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={syncSecretBusy}
                        onClick={() => handleRegenerateSyncSecret(activePartner)}
                      >
                        {syncSecretBusy ? <Loader2 className="animate-spin" /> : <KeyRound />}
                        {activePartner.has_sync_secret ? "Vernieuwen" : "Genereren"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vestigingen */}
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-black tracking-[-0.01em]">Vestigingen</h3>
                  <span className="rounded-pill bg-waves-mist px-2.5 py-0.5 font-mono text-xs font-semibold">{partnerBranches.length}</span>
                  {!isAddingHere && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-auto"
                      onClick={() => {
                        setFormError("");
                        setAddingBranch({ partnerId: activePartner.id, name: "", location: "" });
                      }}
                    >
                      <Plus />
                      Vestiging toevoegen
                    </Button>
                  )}
                </div>

                {partnerBranches.length === 0 && !isAddingHere ? (
                  <div className="rounded-card border border-waves-line bg-white">
                    <EmptyState
                      icon={<MapPin />}
                      title="Geen vestigingen"
                      description="Voeg een vestiging toe voor deze partner."
                    />
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {partnerBranches.map((branch) =>
                      isEditingBranch(branch) && editing ? (
                        <div key={branch.id} className="flex flex-col gap-3 rounded-[16px] border border-waves-zee bg-white px-5 py-4 sm:flex-row sm:items-center">
                          <Input
                            className="sm:flex-1"
                            value={editing.value}
                            onChange={(e) => setEditing((prev) => (prev ? { ...prev, value: e.target.value } : prev))}
                            placeholder="Vestigingsnaam"
                            aria-label="Vestigingsnaam"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveBranch(branch);
                              if (e.key === "Escape") setEditing(null);
                            }}
                          />
                          <Input
                            className="sm:flex-1"
                            value={editing.location}
                            onChange={(e) => setEditing((prev) => (prev ? { ...prev, location: e.target.value } : prev))}
                            placeholder="Locatienaam"
                            aria-label="Locatienaam"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveBranch(branch);
                              if (e.key === "Escape") setEditing(null);
                            }}
                          />
                          <div className="flex shrink-0 justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => setEditing(null)} title="Annuleren">
                              <X />
                            </Button>
                            <Button variant="ink" size="icon" onClick={() => handleSaveBranch(branch)} disabled={saving} title="Opslaan">
                              {saving ? <Loader2 className="animate-spin" /> : <Check />}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div key={branch.id} className="flex items-center gap-4 rounded-[16px] border border-waves-line bg-white px-5 py-4">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-waves-mist text-waves-ink">
                            <MapPin className="h-4 w-4" />
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <div className="truncate text-[15px] font-bold">{branch.name}</div>
                            {branch.location && <div className="truncate text-[13px] text-waves-muted">{branch.location}</div>}
                          </div>
                          <div className="flex shrink-0 gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Bewerken"
                              onClick={() => setEditing({ type: "branch", id: branch.id, value: branch.name, location: branch.location })}
                            >
                              <Pencil />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Verwijderen"
                              className="text-status-alert-fg hover:bg-status-alert-bg"
                              onClick={() =>
                                setConfirmState({
                                  title: "Vestiging verwijderen?",
                                  description: `Vestiging "${branch.name}" verwijderen? Dit kan niet ongedaan worden gemaakt.`,
                                  confirmLabel: "Verwijderen",
                                  onConfirm: () => handleDeleteBranch(branch),
                                })
                              }
                            >
                              <Trash2 />
                            </Button>
                          </div>
                        </div>
                      ),
                    )}

                    {isAddingHere && addingBranch && (
                      <div className="flex flex-col gap-3 rounded-[16px] border border-dashed border-waves-zee bg-waves-subtle px-5 py-4 sm:flex-row sm:items-center">
                        <Input
                          className="sm:flex-1"
                          value={addingBranch.name}
                          onChange={(e) => setAddingBranch((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
                          placeholder="Vestigingsnaam"
                          aria-label="Vestigingsnaam"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleAddBranch();
                            if (e.key === "Escape") { setAddingBranch(null); setFormError(""); }
                          }}
                        />
                        <Input
                          className="sm:flex-1"
                          value={addingBranch.location}
                          onChange={(e) => setAddingBranch((prev) => (prev ? { ...prev, location: e.target.value } : prev))}
                          placeholder="Locatienaam (optioneel)"
                          aria-label="Locatienaam"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleAddBranch();
                            if (e.key === "Escape") { setAddingBranch(null); setFormError(""); }
                          }}
                        />
                        <div className="flex shrink-0 justify-end gap-1">
                          <Button variant="ghost" size="icon" title="Annuleren" onClick={() => { setAddingBranch(null); setFormError(""); }}>
                            <X />
                          </Button>
                          <Button variant="ink" size="icon" title="Toevoegen" onClick={handleAddBranch} disabled={saving}>
                            {saving ? <Loader2 className="animate-spin" /> : <Check />}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add partner dialog */}
      <Dialog open={showPartnerDialog} onOpenChange={(open) => { if (!open) closePartnerDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Partner toevoegen</DialogTitle>
            <DialogDescription>Gebruikers met dit e-maildomein horen bij deze partner.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-partner-name">
                Partnernaam <span className="text-status-alert-fg">*</span>
              </Label>
              <Input
                id="new-partner-name"
                value={newPartnerName}
                onChange={(e) => setNewPartnerName(e.target.value)}
                placeholder="Bijv. Heijmans"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") handleAddPartner(); }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-partner-domain">
                E-maildomein <span className="text-status-alert-fg">*</span>
              </Label>
              <Input
                id="new-partner-domain"
                className="font-mono"
                value={newPartnerEmailDomain}
                onChange={(e) => setNewPartnerEmailDomain(e.target.value)}
                placeholder="Bijv. heijmans.nl"
                onKeyDown={(e) => { if (e.key === "Enter") handleAddPartner(); }}
              />
            </div>
            {formError && (
              <div className="rounded-nav bg-status-alert-bg px-4 py-2.5 text-sm font-medium text-status-alert-fg">{formError}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePartnerDialog}>Annuleren</Button>
            <Button onClick={handleAddPartner} disabled={saving}>
              {saving ? (<><Loader2 className="animate-spin" />Opslaan…</>) : "Partner aanmaken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal-once sync secret dialog */}
      <Dialog
        open={revealedSyncSecret !== null}
        onOpenChange={(open) => { if (!open) { setRevealedSyncSecret(null); setSecretCopied(false); } }}
      >
        <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Nieuwe sync secret</DialogTitle>
          </DialogHeader>
          <div className="rounded-nav bg-status-warn-bg px-4 py-2.5 text-sm font-medium text-status-warn-fg">
            Deze waarde wordt maar één keer getoond. Kopieer hem nu naar het Google Apps Script (header <code className="font-mono">X-Sync-Secret</code>).
          </div>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={revealedSyncSecret ?? ""}
              className="flex-1 bg-waves-subtle font-mono text-sm"
              onFocus={(e) => e.target.select()}
            />
            <Button
              variant="outline"
              size="icon"
              title="Kopiëren"
              onClick={() => {
                if (!revealedSyncSecret) return;
                navigator.clipboard?.writeText(revealedSyncSecret).then(() => {
                  setSecretCopied(true);
                }).catch(() => {});
              }}
            >
              {secretCopied ? <Check className="text-status-ok-fg" /> : <Copy />}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ink" onClick={() => { setRevealedSyncSecret(null); setSecretCopied(false); }}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generic confirm dialog (deletes, secret regenerate/revoke) */}
      <Dialog open={confirmState !== null} onOpenChange={(open) => { if (!open) setConfirmState(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmState?.title}</DialogTitle>
            <DialogDescription>{confirmState?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmState(null)}>Annuleren</Button>
            <Button
              variant="destructive"
              onClick={() => {
                const action = confirmState?.onConfirm;
                setConfirmState(null);
                action?.();
              }}
            >
              {confirmState?.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
